import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sortByCategoryOrder } from './categoryOrder';

export interface ClientRecapGuest {
    name: string;
    tableNumber?: string | null;
    /** Choix par catégorie (ex: {"Entrée": "Caviar d'aubergine", "Plat": "Lieu Noir (Sans sauce)"}) — permet
     *  de présenter le récap en colonnes Entrée/Plat/Dessert plutôt qu'une liste à virgules. */
    dishesByCategory: Record<string, string>;
    /** Allergies déclarées (les plus critiques, listées en premier). */
    allergies?: string[];
    /** Régimes et intolérances (sans lactose, halal, végétarien...). */
    restrictions?: string[];
}

interface ClientRecapPdfData {
    eventLabel: string;
    eventDate?: string;
    guests: ClientRecapGuest[];
    issuerName?: string;
    logoUrl?: string | null;
    brandColor?: string;
}

const detectImageFormat = (url: string): 'PNG' | 'JPEG' | 'WEBP' => {
    if (!url) return 'PNG';
    const lower = url.toLowerCase();
    if (lower.includes('image/png') || lower.endsWith('.png')) return 'PNG';
    if (lower.includes('image/webp') || lower.endsWith('.webp')) return 'WEBP';
    if (lower.includes('image/jpg') || lower.includes('image/jpeg') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'JPEG';
    return 'PNG';
};

const hexToRgb = (hex?: string): [number, number, number] => {
    const clean = (hex || '#6d28d9').replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;
    return [r, g, b];
};

const getContrastRgb = (hex?: string): [number, number, number] => {
    const [r, g, b] = hexToRgb(hex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? [0, 0, 0] : [255, 255, 255];
};

const darken = (rgb: [number, number, number], amount: number): [number, number, number] =>
    rgb.map((c) => Math.max(0, Math.round(c * (1 - amount)))) as [number, number, number];

// Fiche récap CLIENT (Jour J, Phase 5) — ce que les convives ont choisi, groupé par table, avec une colonne
// dédiée par catégorie (Entrée/Plat/Dessert...) plutôt qu'une liste à virgules dans une seule colonne — trop
// dense pour un coup d'œil rapide. Format paysage pour laisser assez de place à chaque colonne. Demande
// explicite d'Emeni (2026-08-23) : "je veux bien qu'on sépare entrée plat et dessert, si tu trouves que
// c'est mieux de mettre en format paysage alors fais ainsi".
export const generateClientRecapPdf = async (data: ClientRecapPdfData) => {
    try {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = 297;
        const marginX = 15;
        const contentW = pageW - marginX * 2;

        const primary = hexToRgb(data.brandColor || '#6d28d9');
        const primary2 = darken(primary, 0.28);
        const onPrimary = getContrastRgb(data.brandColor || '#6d28d9');
        const ink: [number, number, number] = [34, 25, 51];
        const inkFaint: [number, number, number] = [138, 131, 160];
        const tint: [number, number, number] = [244, 241, 252];
        const stripe: [number, number, number] = [250, 248, 253];
        const border: [number, number, number] = [228, 223, 242];

        const headerH = 30;
        const bands = 60;
        for (let i = 0; i < bands; i++) {
            const t = i / (bands - 1);
            const r = Math.round(primary[0] + (primary2[0] - primary[0]) * t);
            const g = Math.round(primary[1] + (primary2[1] - primary[1]) * t);
            const b = Math.round(primary[2] + (primary2[2] - primary[2]) * t);
            doc.setFillColor(r, g, b);
            doc.rect((pageW / bands) * i, 0, pageW / bands + 0.5, headerH, 'F');
        }

        let logoDrawW = 0;
        if (data.logoUrl) {
            const maxW = 22, maxH = 13;
            let drawW = maxW, drawH = maxH;
            try {
                const props = doc.getImageProperties(data.logoUrl);
                const ratio = props.width / props.height;
                if (ratio > maxW / maxH) { drawW = maxW; drawH = maxW / ratio; }
                else { drawH = maxH; drawW = maxH * ratio; }
            } catch (e) { /* repli sur le texte seul */ }
            try {
                doc.addImage(data.logoUrl, detectImageFormat(data.logoUrl), marginX, 9, drawW, drawH);
                logoDrawW = drawW + 6;
            } catch (e) { console.warn('Could not render logo in PDF:', e); }
        }

        doc.setTextColor(onPrimary[0], onPrimary[1], onPrimary[2]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text((data.issuerName || 'Récapitulatif').toUpperCase(), marginX + logoDrawW, 19.5);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        const badgeLabel = 'RÉCAP CONVIVES';
        const badgeW = doc.getTextWidth(badgeLabel) + 12;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(pageW - marginX - badgeW, 10, badgeW, 9, 4.5, 4.5, 'F');
        doc.setTextColor(primary2[0], primary2[1], primary2[2]);
        doc.text(badgeLabel, pageW - marginX - badgeW / 2, 15.7, { align: 'center' });

        let y = headerH + 12;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(ink[0], ink[1], ink[2]);
        doc.text(data.eventLabel || 'Privatisation', marginX, y);
        y += 6;

        if (data.eventDate) {
            const dateLabel = format(new Date(data.eventDate), 'EEEE d MMMM yyyy', { locale: fr });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9.5);
            doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
            doc.text(dateLabel, marginX, y);
            y += 9;
        } else {
            y += 3;
        }

        const categories = sortByCategoryOrder(Array.from(new Set(data.guests.flatMap((g) => Object.keys(g.dishesByCategory)))));
        // Colonne « Intolérance et allergie » : le client repartait avec la liste des plats sans savoir qui ne
        // mange pas quoi. Demande d'Emeni (2026-08-24). Les allergies passent avant les régimes : ce sont
        // elles qui engagent la sécurité du convive.
        const dietLabel = (g: ClientRecapGuest) => {
            const parts = [...(g.allergies || []), ...(g.restrictions || [])].filter(Boolean);
            return parts.length ? parts.join(' · ') : '—';
        };

        const nameColW = 44, tableColW = 14, dietColW = 52;
        const dishColW = categories.length > 0 ? (contentW - nameColW - tableColW - dietColW) / categories.length : 0;
        const dietColX = marginX + nameColW + tableColW + categories.length * dishColW;
        // Interlignage et marges internes fixés ici plutôt que laissés au moteur PDF : la hauteur de ligne
        // réelle de jsPDF (taille × 1,15) ne correspondait pas à celle supposée par le calcul de hauteur de
        // rangée, si bien qu'un plat sur deux lignes débordait sur la rangée suivante — d'où les lignes
        // « collées » signalées par Emeni (2026-08-24). Chaque ligne est désormais posée à la main.
        const LINE_H = 4.1, PAD_TOP = 4.4, PAD_BOTTOM = 3.2, BODY_SIZE = 8.5;

        const drawTableHeader = () => {
            doc.setFillColor(tint[0], tint[1], tint[2]);
            doc.rect(marginX, y, contentW, 8, 'F');
            doc.setTextColor(primary2[0], primary2[1], primary2[2]);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            const baseline = y + 5.2;
            doc.text('CONVIVE', marginX + 3, baseline);
            doc.text('TABLE', marginX + nameColW + 2, baseline);
            categories.forEach((cat, i) => doc.text(cat.toUpperCase(), marginX + nameColW + tableColW + i * dishColW + 2, baseline));
            doc.text('INTOLÉRANCE ET ALLERGIE', dietColX + 2, baseline);
            y += 8 + 1.5;
        };

        // Groupé par table (triée numériquement) — les convives sans table déclarée arrivent en dernier.
        const byTable = new Map<string, ClientRecapGuest[]>();
        data.guests.forEach((g) => {
            const key = g.tableNumber || '';
            if (!byTable.has(key)) byTable.set(key, []);
            byTable.get(key)!.push(g);
        });
        const tableKeys = Array.from(byTable.keys()).sort((a, b) => {
            if (!a) return 1;
            if (!b) return -1;
            return a.localeCompare(b, 'fr', { numeric: true });
        });

        drawTableHeader();
        let rowIdx = 0;
        tableKeys.forEach((tableKey) => {
            const guestsAtTable = byTable.get(tableKey)!;
            guestsAtTable.forEach((g) => {
                // La police doit être posée AVANT de mesurer : le découpage était auparavant calculé avec la
                // taille de l'en-tête, plus petite, et la première rangée de chaque page débordait donc sur la
                // colonne voisine.
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(BODY_SIZE);
                const cellLines: string[][] = categories.map((cat) => doc.splitTextToSize(g.dishesByCategory[cat] || '—', dishColW - 4));
                const dietLines: string[] = doc.splitTextToSize(dietLabel(g), dietColW - 4);
                const nameLines: string[] = doc.splitTextToSize(g.name, nameColW - 5);
                const lineCount = Math.max(1, nameLines.length, dietLines.length, ...cellLines.map((l) => l.length));
                const rowH = PAD_TOP + lineCount * LINE_H + PAD_BOTTOM;

                if (y + rowH > 193) { doc.addPage(); y = 20; drawTableHeader(); rowIdx = 0; }
                if (rowIdx % 2 === 0) {
                    doc.setFillColor(stripe[0], stripe[1], stripe[2]);
                    doc.rect(marginX, y, contentW, rowH, 'F');
                }
                const put = (lines: string[], x: number) => lines.forEach((line, i) => doc.text(line, x, y + PAD_TOP + i * LINE_H));

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(BODY_SIZE);
                doc.setTextColor(ink[0], ink[1], ink[2]);
                put(nameLines, marginX + 3);

                doc.setFont('helvetica', 'normal');
                doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
                doc.text(tableKey || '—', marginX + nameColW + 2, y + PAD_TOP);

                doc.setTextColor(ink[0], ink[1], ink[2]);
                categories.forEach((cat, i) => put(cellLines[i], marginX + nameColW + tableColW + i * dishColW + 2));

                // Une allergie non signalée est un risque, pas un détail : la colonne est mise en évidence dès
                // qu'il y a quelque chose à lire, et restée discrète quand il n'y a rien.
                const hasDiet = (g.allergies || []).length > 0 || (g.restrictions || []).length > 0;
                if (hasDiet) {
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(primary2[0], primary2[1], primary2[2]);
                } else {
                    doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
                }
                put(dietLines, dietColX + 2);
                doc.setFont('helvetica', 'normal');

                y += rowH;
                rowIdx++;
            });
            // Ligne de séparation fine entre deux tables pour repérer les groupes d'un coup d'œil.
            if (tableKey !== tableKeys[tableKeys.length - 1]) {
                doc.setDrawColor(border[0], border[1], border[2]);
                doc.line(marginX, y + 1.5, marginX + contentW, y + 1.5);
                y += 4;
            }
        });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
        doc.text(`Document généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} — iAmani Connexion`, pageW / 2, 205, { align: 'center' });

        doc.save(`Recap_convives_${(data.eventLabel || 'privatisation').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    } catch (e) {
        console.error('Client Recap PDF Generation Error:', e);
        alert('Erreur lors de la génération du document PDF.');
    }
};
